import React, { useState, useRef } from 'react';
import { BookOpen, PenLine } from 'lucide-react';
import Editor from './Editor';
import { Chapter } from '../types';

interface MainContentProps {
  selectedChapter: Chapter | undefined;
  selectedBookId: string | null;
  chapterTitle: string;
  savingStatus: 'saved' | 'saving' | 'error';
  onChapterTitleChange: (newTitle: string) => void;
  onChapterContentUpdate: (content: string) => void;
  onCreateBook: () => void;
  onCreateChapter: (bookId: string) => void;
}

const EmptyState = ({ selectedBookId, onCreateBook, onCreateChapter }: {
  selectedBookId: string | null;
  onCreateBook: () => void;
  onCreateChapter: (bookId: string) => void;
}) => (
  <div className="h-full flex flex-col items-center justify-center text-center px-4">
    <div className="bg-blue-50 rounded-full p-6 mb-6">
      <BookOpen className="w-12 h-12 text-blue-500" />
    </div>
    <h2 className="text-2xl font-bold text-gray-900 mb-2">Start Your Writing Journey</h2>
    <p className="text-gray-600 mb-8 max-w-md">
      Create your first chapter and begin crafting your story. Your creative journey starts here.
    </p>
    {selectedBookId ? (
      <button
        onClick={() => onCreateChapter(selectedBookId)}
        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        <PenLine className="w-5 h-5 mr-2" />
        Create Your First Chapter
      </button>
    ) : (
      <button
        onClick={onCreateBook}
        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        <BookOpen className="w-5 h-5 mr-2" />
        Create Your First Book
      </button>
    )}
  </div>
);

export default function MainContent({
  selectedChapter,
  selectedBookId,
  chapterTitle,
  savingStatus,
  onChapterTitleChange,
  onChapterContentUpdate,
  onCreateBook,
  onCreateChapter,
}: MainContentProps) {
  const [localTitle, setLocalTitle] = useState(chapterTitle);
  const unsavedChanges = useRef({ title: false, content: false });

  // Update local title when selected chapter changes
  React.useEffect(() => {
    setLocalTitle(chapterTitle);
    unsavedChanges.current.title = false;
  }, [chapterTitle]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalTitle(e.target.value);
    unsavedChanges.current.title = true;
  };

  const handleSave = () => {
    if (unsavedChanges.current.title) {
      onChapterTitleChange(localTitle);
      unsavedChanges.current.title = false;
    }
  };

  const handleContentChange = (content: string) => {
    unsavedChanges.current.content = true;
    onChapterContentUpdate(content);
    
    // If there's an unsaved title, save it too
    if (unsavedChanges.current.title) {
      onChapterTitleChange(localTitle);
      unsavedChanges.current.title = false;
    }
  };

  return (
    <main className="flex-1 bg-white p-6 overflow-y-auto">
      {selectedChapter ? (
        <div className="max-w-3xl mx-auto">
          <input
            type="text"
            value={localTitle}
            onChange={handleTitleChange}
            onBlur={handleSave}
            placeholder="Chapter Title"
            className="w-full text-3xl font-bold mb-2 px-2 py-1 focus:outline-none focus:border-blue-500 leading-normal"
          />
          <Editor
            key={selectedChapter.id}
            initialContent={selectedChapter.content}
            onUpdate={handleContentChange}
            saveStatus={savingStatus}
          />
        </div>
      ) : (
        <EmptyState
          selectedBookId={selectedBookId}
          onCreateBook={onCreateBook}
          onCreateChapter={onCreateChapter}
        />
      )}
    </main>
  );
}